import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import API from '../BaseURL';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Menu, MenuItem, Divider, Tooltip, useMediaQuery, useTheme,
  Badge, Chip, InputBase,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  People as UsersIcon,
  Security as RolesIcon,
  Business as SchoolsIcon,
  AccountTree as HubIcon,
  Settings as SettingsIcon,
  History as AuditIcon,
  Assessment as ReportsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  NotificationsNone as NotifIcon,
  PushPin as PinIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowRight as ArrowIcon,
  ExpandMore as ChevronDown,
  ChevronRight as ChevronRight,
  PlaylistAddCheck as KioskIcon,
  AdminPanelSettingsOutlined as MgmtIcon,
  HowToRegOutlined as RegistrationIcon,
  SchoolOutlined as ExamSectionIcon,
  MonetizationOnOutlined as FinanceIcon,
  GroupsOutlined as HrIcon,
  DisplaySettingsOutlined as SystemIcon,
  QueryStatsOutlined as ReportsSectionIcon,
} from '@mui/icons-material';
import { Collapse } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import SchoolSwitcher from '../components/SchoolSwitcher';
import { ASSIGNABLE_PAGES } from '../constants/pages';

// Per-section palette + icon so the drawer headers read as visually
// distinct "shelves" rather than a wall of white text. Each entry
// carries a `from`/`to` pair used for the pill background gradient
// AND a matching accent stripe. Sections not listed here fall back
// to the "default" entry.
const SECTION_STYLES = {
  Management:   { from: '#a78bfa', to: '#6d28d9', accent: '#c4b5fd', Icon: MgmtIcon },
  Registration: { from: '#5eead4', to: '#0f766e', accent: '#99f6e4', Icon: RegistrationIcon },
  Examination:  { from: '#fbbf24', to: '#b45309', accent: '#fde68a', Icon: ExamSectionIcon },
  Finance:      { from: '#34d399', to: '#047857', accent: '#a7f3d0', Icon: FinanceIcon },
  HR:           { from: '#f472b6', to: '#9d174d', accent: '#fbcfe8', Icon: HrIcon },
  System:       { from: '#94a3b8', to: '#334155', accent: '#cbd5e1', Icon: SystemIcon },
  Reports:      { from: '#22d3ee', to: '#0e7490', accent: '#a5f3fc', Icon: ReportsSectionIcon },
  default:      { from: '#94a3b8', to: '#475569', accent: '#cbd5e1', Icon: DashboardIcon },
};
const sectionStyle = (name) => SECTION_STYLES[name] || SECTION_STYLES.default;

const DRAWER_WIDTH = 264;
const RAIL_WIDTH   = 64;
const TRANSITION   = 'cubic-bezier(0.4, 0, 0.2, 1)';

function getNavItems(role, userPages = [], userReports = [], ownerCanManageSchools = false,
                     canOpenSchools = false) {
  const common = [{ label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon fontSize="small" /> }];
  // ASSIGNABLE_PAGES already includes Settings under System; keep it out of the
  // "module" group below and surface it explicitly so super admin always sees it
  // and other users see it only when granted.
  const moduleAccessible = ASSIGNABLE_PAGES.filter(p =>
    p.key !== 'settings'
    && !p.hiddenFromMenu
    && (role === 'super_admin' || userPages.includes(p.key))
  );
  const hasReports = role === 'super_admin' || (userReports && userReports.length > 0);
  const reportsItem = hasReports
    ? [{ label: 'Reports', path: '/reports', icon: <ReportsIcon fontSize="small" />, section: 'Reports' }]
    : [];
  const settingsItem = (role === 'super_admin' || userPages.includes('settings'))
    ? [{ label: 'Settings', path: '/settings', icon: <SettingsIcon fontSize="small" />, section: 'System' }]
    : [];

  // Multi-school link — visible ONLY to the master owner, the single
  // account designated by the first-run setup wizard. Deliberately not
  // tied to the super_admin role: a second school's administrator, or
  // anyone allowed to switch between schools, must not be able to
  // create, rename or deactivate schools. The caller already resolved
  // this against the API (AuthContext.canManageSchools), which also
  // accounts for the vendor flag.
  const schoolsItem = ownerCanManageSchools
    ? [{ label: 'Schools', path: '/schools', icon: <SchoolsIcon fontSize="small" />, section: 'Management' }]
    : [];

  // School Hub — the launcher for stepping into a school. Shown to
  // anyone allowed to open other schools, which is a wider group than
  // those allowed to manage the school list.
  const hubItem = canOpenSchools
    ? [{ label: 'School Hub', path: '/school-hub', icon: <HubIcon fontSize="small" />, section: 'Management' }]
    : [];

  if (role === 'super_admin') {
    return [
      ...common,
      { label: 'Users', path: '/users', icon: <UsersIcon fontSize="small" />, section: 'Management' },
      { label: 'Roles', path: '/roles', icon: <RolesIcon fontSize="small" />, section: 'Management' },
      ...hubItem,
      ...schoolsItem,
      ...moduleAccessible,
      ...reportsItem,
      { label: 'Audit Logs', path: '/audit', icon: <AuditIcon fontSize="small" />, section: 'System' },
      ...settingsItem,
    ];
  }

  return [...common, ...hubItem, ...schoolsItem, ...moduleAccessible, ...reportsItem, ...settingsItem];
}

export default function DashboardLayout() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, schoolName, currentSchool, canManageSchools, canRequestSchool,
          canSwitchSchools, switchSchool } = useAuth();

  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [anchorEl,    setAnchorEl]    = useState(null);
  const [search,      setSearch]      = useState('');
  const [hovering,    setHovering]    = useState(false);
  const [sidebarMode, setSidebarMode] = useState(
    () => localStorage.getItem('sidebarMode') || 'fixed'
  );
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('sidebarCollapsedSections') || '[]')); }
    catch { return new Set(); }
  });

  const toggleSection = (name) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else                next.add(name);
      localStorage.setItem('sidebarCollapsedSections', JSON.stringify([...next]));
      return next;
    });
  };

  const p  = theme.palette.primary.main;
  const p2 = theme.palette.secondary.main;

  const navItems   = getNavItems(
    user?.role, user?.pages || [], user?.reports || [],
    !!(canManageSchools || canRequestSchool),
    !!canSwitchSchools,
  );

  // True when the user has stepped out of the main school. Drives the
  // "you are working in another school" bar below, so nobody can be
  // entering data into the wrong school without noticing.
  const awayFromMain = !!canSwitchSchools
    && !!currentSchool?.id
    && Number(currentSchool.id) !== 1;
  // Prefer the multi-tenant current-school name when available so the
  // header reflects the school the user is currently signed into,
  // falling back to the legacy single-school setup name.
  const displaySchoolName = currentSchool?.name || schoolName;

  // Pending-approval counts shown as red badges next to relevant menu
  // items (Cashbook Approvals, Petty Approvals, Collector Approvals,
  // etc.). Fetched once on mount and refreshed every 60s — also re-
  // fetches when the tab regains focus so newly-approved items vanish
  // immediately when you switch back to the app.
  const [pending, setPending] = useState({});
  const pollRef = useRef(null);
  const refreshPending = useCallback(async () => {
    try {
      const r = await API.get('/approvals/pending-counts');
      setPending(r.data || {});
    } catch { /* silent — endpoint not available on legacy DBs */ }
  }, []);
  useEffect(() => {
    refreshPending();
    pollRef.current = setInterval(refreshPending, 60_000);
    const onFocus = () => refreshPending();
    // Pages that approve/reject items dispatch this event so the
    // sidebar badge updates immediately instead of waiting for the
    // next 60s poll.
    const onPendingChange = () => refreshPending();
    window.addEventListener('focus', onFocus);
    window.addEventListener('pendingCountsChanged', onPendingChange);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pendingCountsChanged', onPendingChange);
    };
  }, [refreshPending]);

  // Kiosk mode for collector users — no sidebar, no top app bar; the page
  // itself owns its header. Default-on for users whose only page is
  // collector_sheet, but it's a *preference* — they can flip back to the
  // normal layout via the in-app toggle, and other users with the collector
  // page can opt-in to it. Persisted in localStorage and sync'd via a
  // custom 'kioskModeChange' event for cross-component updates.
  const canUseCollector = user && (
    user.role === 'super_admin' || (user.pages || []).includes('collector_sheet')
  );
  const isCollectorOnly = user
    && user.role !== 'super_admin'
    && (user.pages || []).length === 1
    && (user.pages || [])[0] === 'collector_sheet';
  const [kioskOverride, setKioskOverride] = useState(() => localStorage.getItem('kioskMode'));
  useEffect(() => {
    const sync = () => setKioskOverride(localStorage.getItem('kioskMode'));
    window.addEventListener('kioskModeChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('kioskModeChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  // Kiosk-by-default for collection screens. Anyone who can collect
  // lands in kiosk view (no sidebar, page fills the screen) when
  // they're on /collect or /collections/daily, so phones and tablets
  // get the whole real estate. On every other page kiosk is only on
  // when explicitly preferred OR the user has nothing but the
  // collector page. Users can still flip to the normal menu via the
  // in-page toggle — the preference is sticky.
  const onCollectionRoute = ['/collect', '/collections/daily']
    .some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  const isKiosk = canUseCollector && (
    kioskOverride === 'kiosk'
    || (kioskOverride !== 'normal' && (isCollectorOnly || onCollectionRoute))
  );
  const setKiosk = (value) => {
    if (value === null) localStorage.removeItem('kioskMode');
    else localStorage.setItem('kioskMode', value ? 'kiosk' : 'normal');
    window.dispatchEvent(new Event('kioskModeChange'));
    if (value) navigate('/collect');
  };

  const isFixed    = sidebarMode === 'fixed';
  const isExpanded = isFixed || hovering;
  const sidebarW   = isKiosk ? 0 : (isExpanded ? DRAWER_WIDTH : RAIL_WIDTH);
  const contentML  = isKiosk ? 0 : (isMobile ? 0 : (isFixed ? DRAWER_WIDTH : RAIL_WIDTH));

  const toggleMode = () => {
    const next = isFixed ? 'floating' : 'fixed';
    setSidebarMode(next);
    localStorage.setItem('sidebarMode', next);
    if (next === 'floating') { setSearch(''); setHovering(false); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleNav = useCallback((path) => {
    navigate(path);
    setMobileOpen(false);
  }, [navigate]);

  const q = search.trim().toLowerCase();
  const filteredItems = q
    ? navItems.filter(it => it.label.toLowerCase().includes(q))
    : navItems;

  // Render a single nav row (used both inside and outside collapsible groups).
  const renderNavRow = (item, expanded) => {
    const active = location.pathname === item.path ||
                   location.pathname.startsWith(item.path + '/');
    // Pending count for this menu entry (when any). The key on each
    // nav item matches a key in /api/approvals/pending-counts.
    const count = Number(pending[item.key] || 0);
    return (
      <ListItem key={item.path} disablePadding
        sx={{ px: expanded ? 1.5 : 1, mb: 0.25, transition: `padding 0.28s ${TRANSITION}` }}>
        <Tooltip title={expanded ? (count > 0 ? `${item.label} · ${count} pending` : '') : item.label} placement="right" arrow>
          <ListItemButton
            onClick={() => handleNav(item.path)}
            sx={{
              borderRadius: 2,
              py: expanded ? 0.9 : 1.1,
              px: expanded ? 1.5 : 0,
              minHeight: 42,
              justifyContent: expanded ? 'flex-start' : 'center',
              bgcolor: active ? 'rgba(255,255,255,0.14)' : 'transparent',
              '&:hover': { bgcolor: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)' },
              position: 'relative',
              transition: `all 0.2s ${TRANSITION}`,
              ...(active && {
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                '&::before': {
                  content: '""', position: 'absolute', left: 0, top: '18%', bottom: '18%',
                  width: 3, borderRadius: '0 3px 3px 0', bgcolor: '#fff',
                },
              }),
            }}
          >
            <ListItemIcon sx={{
              color: active ? '#fff' : 'rgba(255,255,255,0.6)',
              minWidth: expanded ? 34 : 0,
              justifyContent: 'center',
              transition: `min-width 0.28s ${TRANSITION}`,
            }}>
              {count > 0 ? (
                <Badge badgeContent={count} color="error"
                  overlap="circular"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 9, fontWeight: 800, minWidth: 16, height: 16,
                      padding: '0 4px',
                      boxShadow: '0 0 0 1.5px #1565C0',
                    },
                  }}>
                  {item.icon}
                </Badge>
              ) : item.icon}
            </ListItemIcon>
            <Box sx={{
              flex: 1, minWidth: 0,
              opacity: expanded ? 1 : 0,
              maxWidth: expanded ? 160 : 0,
              overflow: 'hidden',
              transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
              display: 'flex', alignItems: 'center',
            }}>
              <Typography sx={{
                fontSize: 13.5,
                fontWeight: active ? 700 : 400,
                color: active ? '#fff' : 'rgba(255,255,255,0.82)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                lineHeight: 1,
              }}>
                {item.label}
              </Typography>
            </Box>
            {count > 0 && expanded && (
              <Box sx={{
                ml: 0.5, mr: active ? 0.3 : 0,
                minWidth: 18, height: 18, px: 0.7,
                borderRadius: 999,
                bgcolor: '#e53935', color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 10, lineHeight: 1,
              }}>
                {count}
              </Box>
            )}
            {active && expanded && (
              <ArrowIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', flexShrink: 0, ml: 0.5 }} />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const renderNavItems = (expanded) => {
    if (filteredItems.length === 0 && q) {
      return [
        <Box key="no-results" sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
            No results for "{search}"
          </Typography>
        </Box>,
      ];
    }

    // When the sidebar is collapsed (rail) or the user is searching, render
    // everything flat — section headers don't make sense in those modes.
    if (!expanded || q) {
      return filteredItems.map((item) => renderNavRow(item, expanded));
    }

    // Group items into sections preserving insertion order
    const groups = [];
    const seen = new Map(); // section name → groups index
    const noSectionItems = [];
    filteredItems.forEach((item) => {
      if (!item.section) { noSectionItems.push(item); return; }
      let idx = seen.get(item.section);
      if (idx === undefined) {
        idx = groups.length;
        seen.set(item.section, idx);
        groups.push({ name: item.section, items: [] });
      }
      groups[idx].items.push(item);
    });

    const els = [];
    noSectionItems.forEach(it => els.push(renderNavRow(it, true)));

    groups.forEach((g, gi) => {
      const isCollapsed = collapsedSections.has(g.name);
      const hasActive   = g.items.some(it =>
        location.pathname === it.path || location.pathname.startsWith(it.path + '/'));
      const st = sectionStyle(g.name);
      const { Icon: SectionIcon } = st;
      els.push(
        <Box key={`div-${g.name}`}
          sx={{ mx: 2, mt: gi === 0 && noSectionItems.length === 0 ? 0.6 : 1.6 }} />
      );
      els.push(
        <ListItemButton key={`sec-${g.name}`}
          onClick={() => toggleSection(g.name)}
          sx={{
            position: 'relative',
            mx: 1.4, mt: 0.4, mb: 0.6,
            px: 1.2, py: 0.7, pr: 1,
            borderRadius: 1.6, overflow: 'hidden',
            color: '#fff',
            background: `linear-gradient(90deg, ${alpha(st.from, 0.32)} 0%, ${alpha(st.to, 0.18)} 100%)`,
            boxShadow: `inset 3px 0 0 ${st.accent}`,
            border: '1px solid ' + alpha(st.accent, 0.28),
            transition: 'transform 0.18s, box-shadow 0.18s, background 0.18s',
            '&:hover': {
              background: `linear-gradient(90deg, ${alpha(st.from, 0.44)} 0%, ${alpha(st.to, 0.26)} 100%)`,
              boxShadow: `inset 3px 0 0 ${st.accent}, 0 4px 12px ${alpha(st.to, 0.28)}`,
            },
          }}>
          <Box sx={{
            width: 24, height: 24, mr: 1,
            borderRadius: 1.2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${st.from} 0%, ${st.to} 100%)`,
            color: '#fff',
            boxShadow: `0 2px 6px ${alpha(st.to, 0.4)}`,
            flexShrink: 0,
          }}>
            <SectionIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography sx={{
            flex: 1, color: '#fff',
            fontWeight: 800, fontSize: 12, letterSpacing: 1.4,
            textTransform: 'uppercase',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          }}>
            {g.name}
          </Typography>
          {hasActive && (
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%',
              bgcolor: st.accent,
              boxShadow: `0 0 8px ${st.accent}`,
              mr: 0.6,
            }} />
          )}
          {isCollapsed
            ? <ChevronRight sx={{ fontSize: 18, color: alpha('#fff', 0.85) }} />
            : <ChevronDown  sx={{ fontSize: 18, color: alpha('#fff', 0.85) }} />}
        </ListItemButton>
      );
      els.push(
        <Collapse key={`col-${g.name}`} in={!isCollapsed} timeout="auto" unmountOnExit>
          <Box sx={{
            mx: 1.4, pl: 0.6, mt: -0.3, mb: 0.4,
            borderLeft: `2px solid ${alpha(st.accent, 0.35)}`,
          }}>
            {g.items.map(it => renderNavRow(it, true))}
          </Box>
        </Collapse>
      );
    });

    return els;
  };

  const drawerContent = (forceExpanded = false) => {
    const exp = forceExpanded || isExpanded;
    return (
      <Box sx={{
        height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: `linear-gradient(170deg, ${p2} 0%, ${p} 55%, ${alpha(p, 0.85)} 100%)`,
      }}>
        <Box sx={{
          px: exp ? 2 : 1, pt: 2.5, pb: exp ? 2 : 1.5,
          display: 'flex', alignItems: 'center', gap: 1.5,
          transition: `padding 0.28s ${TRANSITION}`,
          minHeight: 72,
        }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2.5, flexShrink: 0,
            background: 'rgba(255,255,255,0.18)',
            border: '1.5px solid rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 17, lineHeight: 1 }}>
              {(displaySchoolName || 'S')[0]?.toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{
            flex: 1, minWidth: 0,
            opacity: exp ? 1 : 0,
            maxWidth: exp ? 160 : 0,
            overflow: 'hidden',
            transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
          }}>
            <Typography sx={{
              color: '#fff', fontWeight: 800, fontSize: 13.5, lineHeight: 1.25,
              whiteSpace: 'nowrap', letterSpacing: 0.2,
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {displaySchoolName || 'School System'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 10, letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
              Management Portal
            </Typography>
          </Box>
          {!isMobile && (
            <Tooltip title={isFixed ? 'Switch to floating sidebar' : 'Pin sidebar (always visible)'} placement="right">
              <IconButton size="small" onClick={toggleMode}
                sx={{
                  flexShrink: 0,
                  opacity: exp ? 1 : 0,
                  pointerEvents: exp ? 'auto' : 'none',
                  color: isFixed ? '#fff' : 'rgba(255,255,255,0.45)',
                  bgcolor: isFixed ? 'rgba(255,255,255,0.18)' : 'transparent',
                  border: `1px solid ${isFixed ? 'rgba(255,255,255,0.3)' : 'transparent'}`,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                  width: 28, height: 28,
                  transform: isFixed ? 'rotate(-45deg)' : 'none',
                  transition: `transform 0.25s ease, opacity 0.18s ease`,
                }}>
                <PinIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{
          px: 1.5,
          overflow: 'hidden',
          maxHeight: exp ? 56 : 0,
          opacity: exp ? 1 : 0,
          transition: `max-height 0.28s ${TRANSITION}, opacity 0.2s ${TRANSITION}`,
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2.5,
            px: 1.5, py: 0.9, mb: 1.5,
            '&:focus-within': {
              bgcolor: 'rgba(255,255,255,0.16)',
              border: '1px solid rgba(255,255,255,0.28)',
            },
            transition: 'all 0.15s',
          }}>
            <SearchIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
            <InputBase
              placeholder="Search menu…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{
                flex: 1, fontSize: 12.5, color: '#fff',
                '& input': { p: 0 },
                '& input::placeholder': { color: 'rgba(255,255,255,0.35)', fontSize: 12.5 },
              }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch('')}
                sx={{ p: 0.2, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ mx: exp ? 2 : 1, borderTop: '1px solid rgba(255,255,255,0.08)',
          transition: `margin 0.28s ${TRANSITION}` }} />

        <List sx={{
          flex: 1, pt: 1.5, pb: 1,
          overflowY: 'auto', overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.18)', borderRadius: 4 },
        }}>
          {renderNavItems(exp)}
        </List>

        <Box sx={{ mx: exp ? 2 : 1, borderTop: '1px solid rgba(255,255,255,0.08)',
          transition: `margin 0.28s ${TRANSITION}` }} />

        {canUseCollector && (
          <Box sx={{ px: exp ? 1.75 : 1, pt: 1, pb: 0 }}>
            <Tooltip title={exp ? '' : 'Switch to collector kiosk view'} placement="right" arrow>
              <Box onClick={() => setKiosk(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: exp ? 1.2 : 0,
                  p: exp ? 1 : 1, borderRadius: 2,
                  bgcolor: 'rgba(105,240,174,0.10)',
                  border: '1px solid rgba(105,240,174,0.28)',
                  color: '#b9f6ca',
                  cursor: 'pointer', justifyContent: exp ? 'flex-start' : 'center',
                  '&:hover': { bgcolor: 'rgba(105,240,174,0.18)' },
                  transition: `all 0.2s ${TRANSITION}`,
                }}>
                <KioskIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                <Box sx={{
                  minWidth: 0, opacity: exp ? 1 : 0,
                  maxWidth: exp ? 200 : 0, overflow: 'hidden',
                  transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
                }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                    Use kiosk view
                  </Typography>
                  <Typography sx={{ fontSize: 10, opacity: 0.75, whiteSpace: 'nowrap' }}>
                    Hide menu — booklet only
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Box>
        )}

        <Box sx={{ p: exp ? 1.75 : 1, transition: `padding 0.28s ${TRANSITION}` }}>
          <Tooltip title={exp ? '' : user?.full_name} placement="right" arrow>
            <Box
              onClick={e => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex', alignItems: 'center',
                gap: exp ? 1.5 : 0,
                p: exp ? 1.25 : 1,
                borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
                justifyContent: exp ? 'flex-start' : 'center',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                transition: `all 0.2s ${TRANSITION}`,
              }}
            >
              <Avatar sx={{
                width: 32, height: 32, flexShrink: 0,
                bgcolor: 'rgba(255,255,255,0.22)',
                border: '1.5px solid rgba(255,255,255,0.35)',
                fontSize: 13, fontWeight: 700, color: '#fff',
              }}>
                {user?.full_name?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{
                flex: 1, minWidth: 0,
                opacity: exp ? 1 : 0,
                maxWidth: exp ? 160 : 0,
                overflow: 'hidden',
                transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
              }}>
                <Typography sx={{
                  color: '#fff', fontWeight: 600, fontSize: 12.5, lineHeight: 1.3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user?.full_name}
                </Typography>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.45)', fontSize: 10.5, lineHeight: 1.2,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user?.role_label || user?.role || 'No role'}
                </Typography>
              </Box>
              {exp && (
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  bgcolor: '#69f0ae', boxShadow: '0 0 6px #69f0ae80',
                }} />
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const currentPage = navItems.find(n => n.path === location.pathname) ||
    [{ path: '/profile', label: 'My Profile' }, { path: '/settings', label: 'Settings' }]
      .find(n => location.pathname.startsWith(n.path));

  // Kiosk mode — render the page full-bleed: no sidebar, no top app bar.
  // A small floating "Show menu" pill in the top-right corner lets the
  // user restore the normal layout from any kiosk page without having
  // to sign out or guess where the toggle lives — users were getting
  // stuck in kiosk because each page had to provide its own escape
  // hatch and most didn't.
  if (isKiosk) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative' }}>
        <Tooltip title="Restore main menu" placement="left" arrow>
          <Box
            onClick={() => setKiosk(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setKiosk(false); }}
            sx={{
              position: 'fixed',
              top: 12,
              right: 12,
              zIndex: theme.zIndex.appBar + 5,
              display: 'flex', alignItems: 'center', gap: 0.8,
              px: 1.4, py: 0.7,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                borderColor: 'primary.main',
              },
            }}>
            <MenuIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
              Show menu
            </Typography>
          </Box>
        </Tooltip>
        <Box component="main" sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Outlet />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!isMobile && (
        <Box
          onMouseEnter={() => { if (!isFixed) setHovering(true); }}
          onMouseLeave={() => { if (!isFixed) { setHovering(false); setSearch(''); } }}
          sx={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: sidebarW,
            zIndex: theme.zIndex.drawer,
            overflow: 'hidden',
            transition: `width 0.28s ${TRANSITION}`,
            boxShadow: isFixed
              ? '4px 0 28px rgba(0,0,0,0.12)'
              : hovering ? '8px 0 48px rgba(0,0,0,0.3)' : '2px 0 6px rgba(0,0,0,0.1)',
          }}
        >
          {drawerContent(false)}
        </Box>
      )}

      {isMobile && (
        <Drawer
          open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none' } }}
        >
          {drawerContent(true)}
        </Drawer>
      )}

      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        minWidth: 0, bgcolor: 'background.default',
        ml: `${contentML}px`,
        transition: `margin-left 0.28s ${TRANSITION}`,
      }}>
        <AppBar position="sticky" elevation={0} sx={{
          bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider',
          zIndex: theme.zIndex.appBar,
        }}>
          <Toolbar sx={{ gap: 1.5, minHeight: '60px !important' }}>
            {isMobile && (
              <IconButton size="small" edge="start" onClick={() => setMobileOpen(true)}
                sx={{ color: 'text.secondary', mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.disabled" sx={{ fontSize: 11, lineHeight: 1 }}>
                {displaySchoolName || 'School Management System'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: 16, lineHeight: 1.4 }}>
                {currentPage?.label || 'Dashboard'}
              </Typography>
            </Box>
            {/* Multi-school: renders nothing unless this is a
                super-user on a multi-school deployment. */}
            <SchoolSwitcher />
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Chip label={user?.role_label || user?.role || 'No role'} size="small"
                sx={{ bgcolor: `${p}15`, color: p, fontWeight: 600,
                      fontSize: 11, border: `1px solid ${p}30`, borderRadius: 1.5 }} />
            </Box>
            <Tooltip title="Notifications">
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <Badge badgeContent={0} color="error">
                  <NotifIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title={user?.full_name || 'Account'}>
              <IconButton onClick={e => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
                <Avatar sx={{
                  width: 34, height: 34, bgcolor: p,
                  fontSize: 13, fontWeight: 700, border: `2px solid ${p}40`,
                }}>
                  {user?.full_name?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Working inside another school. Deliberately loud and always
            on screen: an admin who forgets which school they opened
            would otherwise enter students or payments against the
            wrong one. One click returns to the main school. */}
        {awayFromMain && (
          <Box sx={{
            px: 2, py: 0.9,
            bgcolor: (t) => alpha(t.palette.warning.main, 0.16),
            borderBottom: '1px solid',
            borderColor: (t) => alpha(t.palette.warning.main, 0.4),
            display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
          }}>
            <SchoolsIcon sx={{ fontSize: 17, color: 'warning.dark' }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
              You are working in {currentSchool?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'warning.dark', flexGrow: 1 }}>
              Everything you save goes to this school.
            </Typography>
            <Chip
              size="small" color="warning" label="Back to Main School"
              onClick={() => switchSchool(1)}
              sx={{ fontWeight: 700, cursor: 'pointer' }}
            />
          </Box>
        )}

        <Menu
          anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2.5, minWidth: 220, mt: 0.5,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid', borderColor: 'divider',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={700}>{user?.full_name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
          <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}
            sx={{ py: 1.2, gap: 1.5, mt: 0.5, borderRadius: 1, mx: 0.5 }}>
            <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2">My Profile</Typography>
          </MenuItem>
          {user?.role === 'super_admin' && (
            <MenuItem onClick={() => { navigate('/settings'); setAnchorEl(null); }}
              sx={{ py: 1.2, gap: 1.5, borderRadius: 1, mx: 0.5 }}>
              <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2">Settings</Typography>
            </MenuItem>
          )}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleLogout}
            sx={{ py: 1.2, gap: 1.5, color: 'error.main', mb: 0.5, borderRadius: 1, mx: 0.5 }}>
            <LogoutIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Sign Out</Typography>
          </MenuItem>
        </Menu>

        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto' }}>
          <Outlet />
        </Box>

        <Box sx={{
          px: { xs: 1.5, md: 3 }, py: 1,
          borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 1, flexWrap: 'wrap',
          bgcolor: '#fff',
        }}>
          <Typography variant="caption" color="text.disabled">
            {schoolName || 'School Management System'} &nbsp;·&nbsp;{' '}
            {new Date().toLocaleDateString('en-GH', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#69f0ae' }} />
            <Typography variant="caption" color="text.disabled">System Online</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
